import * as Dialog from "@radix-ui/react-dialog";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BarChart3,
  Bell,
  CheckCircle2,
  ChevronDown,
  ClipboardCheck,
  FileBarChart,
  Images,
  LayoutDashboard,
  LogOut,
  Menu,
  Megaphone,
  Moon,
  Settings,
  Sun,
  Truck,
  Users,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { supplierApi } from "../../core/api/supplier-api";
import { useAuth } from "../../core/auth/AuthProvider";
import { env } from "../../core/config/env";
import type { JsonRecord, NotificationItem } from "../../core/types/supplier";
import { cn } from "../../lib/cn";
import { Badge } from "../ui/Badge";
import { Button } from "../ui/Button";

const groups = [
  {
    title: "캠페인",
    items: [
      { to: "/", label: "캠페인 개요", icon: LayoutDashboard },
      { to: "/campaigns", label: "캠페인 운영", icon: Megaphone },
      { to: "/applicants", label: "신청자", icon: Users },
      { to: "/selection", label: "정품 배송", icon: CheckCircle2 },
      { to: "/delivery", label: "배송 현황", icon: Truck },
    ],
  },
  {
    title: "설문",
    items: [
      { to: "/survey", label: "설문 현황", icon: ClipboardCheck },
      { to: "/distribution", label: "통계", icon: BarChart3 },
      { to: "/evidence", label: "수령 사진", icon: Images },
    ],
  },
  {
    title: "결과",
    items: [{ to: "/report", label: "설문 결과", icon: FileBarChart }],
  },
  {
    title: "계정",
    items: [{ to: "/settings", label: "설정", icon: Settings }],
  },
];

function targetPath(data?: JsonRecord) {
  const value = String(data?.tab ?? data?.screen ?? data?.type ?? "");
  if (/ai_validation_result|ai-validation-result/.test(value))
    return "/campaigns";
  if (/ai_validation_survey|ai-validation-survey/.test(value))
    return "/campaigns";
  if (/ai_validation_question|ai-validation-question/.test(value))
    return "/campaigns";
  if (/ai_validation|ai-validation/.test(value)) return "/campaigns";
  if (/analytics|reports|report/.test(value)) return "/report";
  if (/sample_request/.test(value)) return "/applicants";
  if (/survey_response|survey_completed|survey/.test(value)) return "/survey";
  if (/products|product|campaigns|campaign/.test(value)) return "/campaigns";
  if (/profile|supplier_status|settings|supplier/.test(value))
    return "/settings";
  return null;
}

function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const location = useLocation();
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    try {
      return JSON.parse(
        localStorage.getItem("supplier_sidebar_groups") ?? "{}",
      ) as Record<string, boolean>;
    } catch {
      return {};
    }
  });

  useEffect(() => {
    const activeGroup = groups.find((group) =>
      group.items.some(
        (item) =>
          location.pathname === item.to ||
          (item.to !== "/" && location.pathname.startsWith(`${item.to}/`)),
      ),
    );
    if (activeGroup) {
      setOpenGroups((current) =>
        current[activeGroup.title] === false
          ? { ...current, [activeGroup.title]: true }
          : current,
      );
    }
  }, [location.pathname]);

  useEffect(() => {
    localStorage.setItem("supplier_sidebar_groups", JSON.stringify(openGroups));
  }, [openGroups]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-20 items-center px-6">
        <img
          src="/brand/kithub-appbar-logo-light.svg"
          alt="KitHub"
          className="h-8 w-auto dark:hidden"
        />
        <img
          src="/brand/kithub-appbar-logo-dark.svg"
          alt="KitHub"
          className="hidden h-8 w-auto dark:block"
        />
      </div>
      <nav className="flex-1 overflow-y-auto px-3 pb-6">
        {groups.map((group) => (
          <div className="mb-4" key={group.title}>
            <button
              type="button"
              className="flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-left text-sm font-black text-[var(--on-surface)] hover:bg-[var(--surface-low)]"
              aria-expanded={openGroups[group.title] !== false}
              onClick={() =>
                setOpenGroups((current) => ({
                  ...current,
                  [group.title]: current[group.title] === false,
                }))
              }
            >
              {group.title}
              <ChevronDown
                className={cn(
                  "size-4 transition-transform",
                  openGroups[group.title] === false && "-rotate-90",
                )}
              />
            </button>
            {openGroups[group.title] !== false ? (
              <div className="nav-children mt-1 grid gap-1">
                {group.items.map(({ to, label, icon: Icon }) => (
                  <NavLink
                    end={to === "/"}
                    key={to}
                    to={to}
                    onClick={onNavigate}
                    className={({ isActive }) =>
                      cn("nav-item", isActive && "nav-item-active")
                    }
                  >
                    <Icon className="size-[18px]" />
                    <span>{label}</span>
                  </NavLink>
                ))}
              </div>
            ) : null}
          </div>
        ))}
      </nav>
      <div className="bg-[var(--surface-low)] p-4 text-xs text-[var(--muted)]">
        <div className="font-bold">v{env.version}</div>
        {env.demoMode ? <Badge tone="info">데모 모드</Badge> : null}
      </div>
    </div>
  );
}

export function AppShell() {
  const { logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [dark, setDark] = useState(
    () =>
      localStorage.getItem("supplier_theme_mode") === "dark" ||
      (!localStorage.getItem("supplier_theme_mode") &&
        matchMedia("(prefers-color-scheme: dark)").matches),
  );

  const unreadQuery = useQuery({
    queryKey: ["notifications", "unread"],
    queryFn: supplierApi.unreadNotificationCount,
    refetchInterval: 60_000,
  });
  const notificationsQuery = useQuery({
    queryKey: ["notifications"],
    queryFn: supplierApi.notifications,
    enabled: notificationsOpen,
  });

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("supplier_theme_mode", dark ? "dark" : "light");
  }, [dark]);

  useEffect(() => setMobileOpen(false), [location.pathname]);

  useEffect(() => {
    const handler = () => {
      void queryClient.invalidateQueries({ queryKey: ["notifications"] });
    };
    window.addEventListener("kithub:push", handler);
    return () => window.removeEventListener("kithub:push", handler);
  }, [queryClient]);

  async function openNotification(item: NotificationItem) {
    if (!item.isRead) {
      await supplierApi.markNotificationRead(item.id);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["notifications"] }),
        queryClient.invalidateQueries({
          queryKey: ["notifications", "unread"],
        }),
      ]);
    }
    const path = targetPath(item.data);
    if (path) navigate(path);
    setNotificationsOpen(false);
  }

  return (
    <div className="app-shell">
      <aside className="desktop-sidebar">
        <Sidebar />
      </aside>

      <Dialog.Root open={mobileOpen} onOpenChange={setMobileOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="dialog-overlay lg:hidden" />
          <Dialog.Content className="fixed inset-y-0 left-0 z-50 w-[min(86vw,310px)] bg-[var(--surface)] shadow-2xl outline-none lg:hidden">
            <Dialog.Title className="sr-only">메뉴</Dialog.Title>
            <Sidebar onNavigate={() => setMobileOpen(false)} />
            <Dialog.Close className="absolute right-3 top-4 grid size-10 place-items-center rounded-full hover:bg-[var(--surface-low)]">
              <X className="size-5" />
            </Dialog.Close>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <div className="min-w-0">
        <header className="topbar">
          <button
            className="icon-button mobile-menu-button"
            aria-label="메뉴 열기"
            onClick={() => setMobileOpen(true)}
          >
            <Menu className="size-5" />
          </button>
          <div className="ml-auto flex items-center gap-1">
            <button
              className="icon-button"
              aria-label={dark ? "라이트 모드" : "다크 모드"}
              onClick={() => setDark((value) => !value)}
            >
              {dark ? <Sun className="size-5" /> : <Moon className="size-5" />}
            </button>
            <button
              className="icon-button relative"
              aria-label="알림"
              onClick={() => setNotificationsOpen(true)}
            >
              <Bell className="size-5" />
              {(unreadQuery.data ?? 0) > 0 ? (
                <span className="absolute right-1 top-1 grid min-w-4 place-items-center rounded-full bg-[var(--danger)] px-1 text-[10px] font-black text-white">
                  {Math.min(unreadQuery.data ?? 0, 99)}
                </span>
              ) : null}
            </button>
          </div>
        </header>
        <main className="page-container">
          <Outlet />
        </main>
      </div>

      <Dialog.Root open={notificationsOpen} onOpenChange={setNotificationsOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="dialog-overlay" />
          <Dialog.Content className="dialog-content max-w-lg">
            <div className="flex items-center justify-between gap-4">
              <Dialog.Title className="text-xl font-black">알림</Dialog.Title>
              <Dialog.Close className="icon-button">
                <X className="size-5" />
              </Dialog.Close>
            </div>
            <div className="mt-5 max-h-[65vh] overflow-y-auto">
              {notificationsQuery.isLoading ? (
                <p className="py-12 text-center text-sm text-[var(--muted)]">
                  알림을 불러오는 중입니다.
                </p>
              ) : (notificationsQuery.data ?? []).length === 0 ? (
                <p className="py-12 text-center text-sm text-[var(--muted)]">
                  받은 알림이 없습니다.
                </p>
              ) : (
                <div className="divide-y divide-[var(--outline-variant)]">
                  {(notificationsQuery.data ?? []).map((item) => (
                    <button
                      key={item.id}
                      className="grid w-full gap-1 px-1 py-4 text-left hover:bg-[var(--surface-low)]"
                      onClick={() => void openNotification(item)}
                    >
                      <span className="flex items-center gap-2 font-extrabold">
                        {!item.isRead ? (
                          <span className="size-2 rounded-full bg-[var(--primary-strong)]" />
                        ) : null}
                        {item.title ?? "알림"}
                      </span>
                      <span className="text-sm leading-6 text-[var(--muted)]">
                        {item.body}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Button
              variant="ghost"
              className="mt-4 w-full"
              icon={<LogOut className="size-4" />}
              onClick={() => void logout()}
            >
              로그아웃
            </Button>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}
